import { Card, Col, Row } from 'antd';
import Box from 'components/Box';
import Text from 'components/Text';
import IconAndPrice, { IIconAndPriceProps } from 'features/report/components/IconAndPrice';
import React, { useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAppDispatch } from 'store/hooks';
import reportSelectors from 'features/report/services/selectors';
import reportActions from 'features/report/services/actions';
import clsx from 'clsx';
import { formatCurrency } from 'utils/formatCurrency';
import { ReportType } from 'features/report/pages/ReportPage/constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ICategoriesChart {
  v2?: boolean;
}
interface IListCategoriesItem extends IIconAndPriceProps { }
const defaultListCategoriesItem: IListCategoriesItem[] = [
  {
    title: 'TOTAL SALES',
    icon: 'moneyExchange',
    percent: 0,
    price: 0,
    increase: false,
    decrease: false,
    isPercentageInTittle: true,
  },
  {
    title: 'AVERAGE TICKET',
    icon: 'moneyExchange',
    percent: 0,
    price: 0,
    increase: false,
    decrease: false,
    isPercentageInTittle: true
  },
  {
    title: 'TOTAL SERVICES',
    percent: 0,
    price: 0,
    increase: false,
    decrease: false,
    isPercentageInTittle: true,
    isHidePercentage: true,
    isHideCurrency: true
  }
];

interface IDataChart {
  labels: string[],
  datasets: IDataset[]
}
interface IDataset {
  data: number[],
  backgroundColor: string,
  barThickness?: number,
  [key: string]: any
}

const CategoriesChart: React.FC<ICategoriesChart> = ({ v2 }) => {
  const dataProductChart = reportSelectors.getDataProductReportV2();
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false
      },
      tooltips: {
        callbacks: {
          label: () => undefined,
        }
      },
    }
  };


  const dataChart = useMemo(() => {
    const getIndexItemHaveMaxValue = () => {
      const maxValue = Math.max(...dataProductChart.map(item => item.sale));
      return dataProductChart.findIndex(item => item.sale?.toString() === maxValue?.toString());
    };
    const indexItemHaveMaxValue = getIndexItemHaveMaxValue();
    return {
      labels: dataProductChart.map(item => item.categoryName),
      datasets: [{
        data: dataProductChart.map(item => item.sale), backgroundColor: (item: any) => {
          if (item.dataIndex === indexItemHaveMaxValue)
            return '#F5767F';
          return '#64748B';
        }, barThickness: 60
      }]
    };
  }, [dataProductChart]);


  return (
    <StyledCard className={v2 ? 'v2' : ''}>
      <Box pt='4' alignItems='center' display='flex' alignSelf='stretch' justifyContent='space-between'>
        <Text variant="H5">CATEGORIES</Text>
        <Text variant="H5">{formatCurrency(dataProductChart.reduce((acc, item) => acc + item.sale, 0))}</Text>
      </Box>
      <Chart>
        <Bar options={options} data={dataChart} style={{ width: '100%', height: 'auto' }} />
      </Chart>
    </StyledCard>
  );
};

export default CategoriesChart;

const StyledCard = styled(Card)`
    flex: 1;
    border-radius: 3.358px;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25),
    inset -1px 1px 4px rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    border: 0.672px solid #86909C;
    background: #FFF;
    &.v2 {
        border: none;
        box-shadow: none;
        padding-right: 16px;
        .ant-card-body {
            padding: 0;
        }
    }
`;

const Chart = styled.div`
    margin: 1rem 0rem;
    min-height: 400px;
    background: #F6F7FC;
`;

const StatisticV2 = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
`;
type StatisticItemProps = {
  value: string;
  label: string;
  increase?: boolean;
  decrease?: boolean;
  percent?: number;
}
const StatisticItem = ({ value, label, decrease, increase, percent }: StatisticItemProps) => {
  return <StatisticItemStyled>
    <div className="statistic-content">
      <div className="statistic-amount">
        <span className="amount">{value}</span>
        <span className="label">{label}</span>
      </div>
      <div className={clsx('statistic-percent', increase && 'increase', decrease && 'decrease')}>
        <span>{decrease ? '-' : '+'}{percent}%</span>
      </div>
    </div>
  </StatisticItemStyled>;
};

const StatisticItemStyled = styled.div`
display: flex;
padding: 0px 16px 0px 24px;
align-items: flex-start;
gap: 16px;
flex: 1 0 0;
border-radius: 4px;
.statistic-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex: 1 0 0;
}

.statistic-amount {
    display: flex;
    flex-direction: column;
    gap: 0px;
    align-self: stretch;
    span.amount {
        flex: 1 0 0;
        color: #1D2129;
        font-family: Poppins;
        font-size: 32px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
    }

    span.label {
        align-self: stretch;
        color: #505050;
        font-family: Poppins;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 30px;
        text-transform: capitalize;
    }
}

.statistic-percent {
    display: flex;
    padding: 0px 8px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 100px;
    background: #E5E6EB;

    span {
        color: #64748B;
        font-family: Poppins;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 30px;
        text-transform: capitalize;

        &.increase {
            color: #2D6772;
        }
        &.decrease {
            color: #D2464F;
        }
    }
}
`;